import { create } from "zustand";
import {
  DEFAULT_CLEARED,
  DEFAULT_CLEARING_HOUSE,
  SWAPTION_DEFAULT_CLEARED,
  SWAPTION_DEFAULT_UNDERLYING_CLEARED,
  returnSwapLegDefaults,
} from "@/lib/businessRules/swapConventionsRule";
import { TradeOpenClose } from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import {
  SwaptionDirection,
  TradeDelivery,
  TradeOptionStyle,
  TradeOptionType,
  TradePremiumType,
  TradeStrikeQuotation,
} from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import {
  SwapInflationDirection,
  TradeInterestType,
} from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import {
  returnDefaultSettlementDate,
  returnTodayISODate,
} from "@/lib/businessRules/settlementDateRules";
import { returnDateFromTenor } from "@/lib/businessRules/tenorRule";

// LQC-2254 / LQC-2493 - the ticket a user fills in by hand, with no OMS.
//
// This mirrors the POST /Trade/{instrument} payload, NOT a block. The earlier
// IDraftBlock modelled the fields the API returns on a block, on the assumption that
// the front end manufactured one. /Trade/Bond disproved that: we send an OMS-shaped
// trade message and the back end builds the block, so the draft is shaped like the
// message we send. See widgets/tradeFromScratch/README.md.
//
// Everything is keyed by an opaque ticketId so two open tickets are independent. The
// Leg Creation widget passes its React Grid Layout cryptoID; Trade Creation draft legs
// will pass their own id.
//
// Each instrument holds a SEPARATE ticket rather than one shape with optional fields:
// the field sets barely overlap, and switching instrument should not silently carry
// a bond nominal onto a future contract count.

/* eslint-disable no-unused-vars */

// The instruments the ticket can write. Only ever instruments with a working ticket -
// this is not the full InstrumentTypeCode vocabulary. See README.
export enum TicketInstrument {
  Bond = "Bond",
  Future = "Future",
  BondOption = "Bond Option",
  Swap = "Swap",
  Swaption = "Swaption",
  Repo = "Repo",
}

export const DEFAULT_TICKET_INSTRUMENT = TicketInstrument.Bond;

export type TicketDirection = TradeDirection.Buy | TradeDirection.Sell | "";

export type SwaptionTicketDirection = SwaptionDirection | "";

export type FutureTicketDirection =
  | TradeDirection.BuyFuture
  | TradeDirection.SellFuture
  | "";

// A portfolio split past the first (LQC-2941 / LQC-2949). The first allocation is the
// ticket's portfolioCode and takes whatever nominal the splits leave, so the
// allocations always sum to the trade nominal.
export interface ITicketAllocation {
  portfolioCode: string;
  nominal: number | null;
  // LQC-3029 - the saved allocation an edit draft's row stands for; absent on a new row.
  allocationId?: number;
}

// Fields every ticket carries, whatever the instrument.
export interface ITicketCommon {
  // External ID (spec F3), sent as tradeDetails.transactionNumber. Optional.
  transactionNumber: string;
  additionalAllocations: ITicketAllocation[];
}

export function returnEmptyTicketCommon(): ITicketCommon {
  return { transactionNumber: "", additionalAllocations: [] };
}

export interface IBondTicket extends ITicketCommon {
  // isin and securityName are two views of one choice - both are always set together
  // from the matching bondSecuritiesStore row, never typed free-hand.
  isin: string;
  securityName: string;
  portfolioCode: string;
  nominal: number | null;
  direction: TicketDirection;
  tradeDate: string; // yyyy-MM-dd
  settlementDate: string; // yyyy-MM-dd
}

export interface IFutureTicket extends ITicketCommon {
  // The one identifier a future needs, and what the outgoing message carries. No name
  // field to match the bond ISIN / securityName pair: a futures SecurityName is the
  // ticker, so it would only ever repeat this.
  bloombergSymbol: string;
  portfolioCode: string;
  // Contract COUNT, not a face value - it still travels as tradeDetails.nominal.
  contractCount: number | null;
  direction: FutureTicketDirection;
  tradeDate: string; // yyyy-MM-dd
  settlementDate: string; // yyyy-MM-dd
}

// A bond option is a bond ticket plus option terms. The underlying is picked from the
// same bondSecuritiesStore the bond ticket uses, so isin / securityName behave the same.
// optionStyle, premiumType and delivery are conventions, defaulted and not asked for.
export interface IBondOptionTicket extends ITicketCommon {
  isin: string;
  securityName: string;
  portfolioCode: string;
  nominal: number | null;
  direction: TicketDirection;
  tradeDate: string; // yyyy-MM-dd
  settlementDate: string; // yyyy-MM-dd
  optionType: TradeOptionType | "";
  // Seeded from the underlying but editable - real data has USD bonds paying EUR.
  currencyPayment: string;
  expiryDate: string; // yyyy-MM-dd
  strike: number | null;
  strikeQuotation: TradeStrikeQuotation;
  optionStyle: TradeOptionStyle;
  premiumType: TradePremiumType;
  delivery: TradeDelivery;
  openClose: TradeOpenClose;
}

// Buy / Sell Inflation are screen-only (LQC-2991), sent as Pay / Receive.
export type SwapTicketDirection =
  | TradeDirection.Pay
  | TradeDirection.Receive
  | SwapInflationDirection
  | "";

// One swap leg. Rates are held as the user types them (percent, e.g. 2.678521) and only
// converted to the wire decimal at build time - the API bounds both to (-1, 1).
export interface ISwapLegTicket {
  interestType: TradeInterestType;
  couponRate: number | null;
  spread: number | null;
  couponFrequency: string;
  resetFrequency: string;
  referenceRate: string;
  currency: string;
  dayCount: string;
  businessDay: string;
  calendar: string;
}

// Leg 1 is the fixed leg and Leg 2 the floating one by convention, but both are
// switchable - the API only cares that a floating leg carries a reference rate.
export interface ISwapTicket extends ITicketCommon {
  // The template swap picked (LQC-2939). Pre-fill only, never sent; "" when none.
  templateName: string;
  portfolioCode: string;
  // Leg 1 nominal, and the trade nominal the API checks allocations against.
  nominal: number | null;
  // Leg 2 nominal, only captured for XCS/XBS (LQC-2971). Otherwise sent as nominal.
  nominal2: number | null;
  direction: SwapTicketDirection;
  tradeDate: string; // yyyy-MM-dd
  settlementDate: string; // yyyy-MM-dd
  startDate: string; // yyyy-MM-dd
  maturityDate: string; // yyyy-MM-dd
  // "" follows startDate; set only once the user overrides it (spec F9).
  firstCouponFrom: string;
  // ZCS only (LQC-2957). The index is derived from the currency, one per currency.
  cpi: number | null;
  // LQC-2957 - the CPI lookup the value came from; "" once the user types their own.
  cpiAutoKey: string;
  cleared: boolean;
  clearingHouse: string;
  openClose: TradeOpenClose;
  leg1: ISwapLegTicket;
  leg2: ISwapLegTicket;
}

// What the widget confirms after a send. Built while the ticket still exists, because
// the send clears it immediately - the confirmation has to outlive what it describes.
export interface ISentBondTicket {
  // "Buy 10 MM GB00BMBL1F74"
  summary: string;
  sentAt: number; // epoch ms - the confirmation life is measured from this
}

// A fresh bond ticket opens dated: today, settling T+2 business days.
export function returnEmptyBondTicket(): IBondTicket {
  const tradeDate = returnTodayISODate();
  return {
    ...returnEmptyTicketCommon(),
    isin: "",
    securityName: "",
    portfolioCode: "",
    nominal: null,
    direction: "",
    tradeDate,
    settlementDate: returnDefaultSettlementDate(tradeDate),
  };
}

// Futures settle same day, not T+N: the front end sends the browser date. LQC-2496
// struck settlement off the reference-data endpoint for exactly this reason. Still
// editable, same as the bond.
export function returnEmptyFutureTicket(): IFutureTicket {
  const tradeDate = returnTodayISODate();
  return {
    ...returnEmptyTicketCommon(),
    bloombergSymbol: "",
    portfolioCode: "",
    contractCount: null,
    direction: "",
    tradeDate,
    settlementDate: tradeDate,
  };
}

// Option conventions are pre-filled, so a fresh ticket only needs the underlying, the
// option type, an expiry, a nominal and a portfolio. Dates match the bond ticket.
export function returnEmptyBondOptionTicket(): IBondOptionTicket {
  const tradeDate = returnTodayISODate();
  return {
    ...returnEmptyTicketCommon(),
    isin: "",
    securityName: "",
    portfolioCode: "",
    nominal: null,
    direction: "",
    tradeDate,
    settlementDate: returnDefaultSettlementDate(tradeDate),
    optionType: "",
    currencyPayment: "",
    expiryDate: "",
    strike: null,
    strikeQuotation: TradeStrikeQuotation.Price,
    optionStyle: TradeOptionStyle.European,
    premiumType: TradePremiumType.Spot,
    delivery: TradeDelivery.Physical,
    openClose: TradeOpenClose.Open,
  };
}

// A swaption is option terms over a swap, so it reuses ISwapLegTicket for the underlying
// (spread is simply unused - the swaption leg model has no such field).
export interface ISwaptionTicket extends ITicketCommon {
  // The underlying's template swap (LQC-2939). Pre-fill only, never sent.
  templateName: string;
  portfolioCode: string;
  nominal: number | null;
  // Carries the option type too (spec F10); split on send.
  direction: SwaptionTicketDirection;
  tradeDate: string;
  settlementDate: string;
  expiryDate: string;
  strike: number | null;
  strikeQuotation: TradeStrikeQuotation;
  exerciseType: TradeOptionStyle;
  premiumType: TradePremiumType;
  delivery: TradeDelivery;
  cleared: boolean;
  currencyPayment: string;
  openClose: TradeOpenClose;
  // The underlying swap.
  startDate: string;
  maturityDate: string;
  // "" follows startDate; set only once the user overrides it (spec F10).
  firstCouponFrom: string;
  underlyingCleared: boolean;
  // ZCS underlying only (LQC-2959), typed until CPI is sourced (B9/B10).
  cpi: number | null;
  // LQC-2957 - the CPI lookup the value came from; "" once the user types their own.
  cpiAutoKey: string;
  leg1: ISwapLegTicket;
  leg2: ISwapLegTicket;
}

export type RepoTicketDirection = TradeDirection.Borrow | TradeDirection.Lend | "";

// LQC-2955 - a repo over a collateral bond picked from bondSecuritiesStore. The start
// date is the settlementDate: the API has no separate field.
export interface IRepoTicket extends ITicketCommon {
  isin: string;
  securityName: string;
  portfolioCode: string;
  nominal: number | null; // face value of the collateral
  direction: RepoTicketDirection;
  tradeDate: string; // yyyy-MM-dd
  settlementDate: string; // yyyy-MM-dd, the repo start
  terminationDate: string; // yyyy-MM-dd
  // Locked Open until the repo close requirement exists.
  openClose: TradeOpenClose;
}

// Starts T+0 and terminates a week later (LEG_CREATION_PLAN Q12).
export const REPO_DEFAULT_TERM = "7D";

export function returnEmptyRepoTicket(): IRepoTicket {
  const tradeDate = returnTodayISODate();
  return {
    ...returnEmptyTicketCommon(),
    isin: "",
    securityName: "",
    portfolioCode: "",
    nominal: null,
    direction: "",
    tradeDate,
    settlementDate: tradeDate,
    terminationDate: returnDateFromTenor(REPO_DEFAULT_TERM, tradeDate),
    openClose: TradeOpenClose.Open,
  };
}

export const DEFAULT_SWAP_CURRENCY = "EUR";

export function returnEmptySwapLeg(
  currency: string,
  interestType: TradeInterestType,
): ISwapLegTicket {
  const defaults = returnSwapLegDefaults(currency, interestType);
  return {
    interestType,
    couponRate: null,
    spread: null,
    currency,
    ...defaults,
  };
}

// Opens as a plain cleared EUR fixed-vs-floating IRS, which is what most of the book is.
// Start date is spot (T+2) and maturity is left for the user - there is no default tenor.
export function returnEmptySwapTicket(): ISwapTicket {
  const tradeDate = returnTodayISODate();
  const settlementDate = returnDefaultSettlementDate(tradeDate);
  return {
    ...returnEmptyTicketCommon(),
    templateName: "",
    portfolioCode: "",
    nominal: null,
    nominal2: null,
    direction: "",
    tradeDate,
    settlementDate,
    startDate: settlementDate,
    maturityDate: "",
    firstCouponFrom: "",
    cpi: null,
    cpiAutoKey: "",
    cleared: DEFAULT_CLEARED,
    clearingHouse: DEFAULT_CLEARING_HOUSE,
    openClose: TradeOpenClose.Open,
    leg1: returnEmptySwapLeg(DEFAULT_SWAP_CURRENCY, TradeInterestType.Fixed),
    leg2: returnEmptySwapLeg(DEFAULT_SWAP_CURRENCY, TradeInterestType.Floating),
  };
}

// Opens on what real swaptions look like: Cash settled, Yield strike, uncleared, over an
// uncleared EUR fixed-vs-floating swap. See SWAPTION_DEFAULT_* for the measurements.
export function returnEmptySwaptionTicket(): ISwaptionTicket {
  const tradeDate = returnTodayISODate();
  const settlementDate = returnDefaultSettlementDate(tradeDate);
  return {
    ...returnEmptyTicketCommon(),
    templateName: "",
    portfolioCode: "",
    nominal: null,
    direction: "",
    tradeDate,
    settlementDate,
    expiryDate: "",
    strike: null,
    strikeQuotation: TradeStrikeQuotation.Yield,
    exerciseType: TradeOptionStyle.European,
    premiumType: TradePremiumType.Spot,
    delivery: TradeDelivery.Physical,
    cleared: SWAPTION_DEFAULT_CLEARED,
    currencyPayment: DEFAULT_SWAP_CURRENCY,
    openClose: TradeOpenClose.Open,
    startDate: settlementDate,
    maturityDate: "",
    firstCouponFrom: "",
    underlyingCleared: SWAPTION_DEFAULT_UNDERLYING_CLEARED,
    cpi: null,
    cpiAutoKey: "",
    leg1: returnEmptySwapLeg(DEFAULT_SWAP_CURRENCY, TradeInterestType.Fixed),
    leg2: returnEmptySwapLeg(DEFAULT_SWAP_CURRENCY, TradeInterestType.Floating),
  };
}

type State = {
  instrumentByTicketId: Record<string, TicketInstrument>;
  bondTicketsByTicketId: Record<string, IBondTicket>;
  futureTicketsByTicketId: Record<string, IFutureTicket>;
  bondOptionTicketsByTicketId: Record<string, IBondOptionTicket>;
  swapTicketsByTicketId: Record<string, ISwapTicket>;
  swaptionTicketsByTicketId: Record<string, ISwaptionTicket>;
  repoTicketsByTicketId: Record<string, IRepoTicket>;
  isSendingByTicketId: Record<string, boolean>;
  lastSentByTicketId: Record<string, ISentBondTicket>;
};

type Action = {
  setTicketInstrument: (ticketId: string, instrument: TicketInstrument) => void;
  ensureBondTicket: (ticketId: string) => void;
  updateBondTicket: (ticketId: string, changes: Partial<IBondTicket>) => void;
  resetBondTicket: (ticketId: string) => void;
  ensureFutureTicket: (ticketId: string) => void;
  updateFutureTicket: (
    ticketId: string,
    changes: Partial<IFutureTicket>,
  ) => void;
  resetFutureTicket: (ticketId: string) => void;
  ensureBondOptionTicket: (ticketId: string) => void;
  updateBondOptionTicket: (
    ticketId: string,
    changes: Partial<IBondOptionTicket>,
  ) => void;
  resetBondOptionTicket: (ticketId: string) => void;
  ensureSwapTicket: (ticketId: string) => void;
  updateSwapTicket: (ticketId: string, changes: Partial<ISwapTicket>) => void;
  // Leg edits go through their own action so a caller cannot drop the other leg.
  updateSwapLeg: (
    ticketId: string,
    leg: "leg1" | "leg2",
    changes: Partial<ISwapLegTicket>,
  ) => void;
  resetSwapTicket: (ticketId: string) => void;
  ensureSwaptionTicket: (ticketId: string) => void;
  updateSwaptionTicket: (
    ticketId: string,
    changes: Partial<ISwaptionTicket>,
  ) => void;
  updateSwaptionLeg: (
    ticketId: string,
    leg: "leg1" | "leg2",
    changes: Partial<ISwapLegTicket>,
  ) => void;
  resetSwaptionTicket: (ticketId: string) => void;
  ensureRepoTicket: (ticketId: string) => void;
  updateRepoTicket: (ticketId: string, changes: Partial<IRepoTicket>) => void;
  resetRepoTicket: (ticketId: string) => void;
  setIsSendingTicket: (ticketId: string, isSending: boolean) => void;
  setLastSentTicket: (ticketId: string, lastSent: ISentBondTicket) => void;
  clearLastSentTicket: (ticketId: string) => void;
  removeTickets: (ticketId: string) => void;
};

const tradeFromScratchStore = create<State & Action>((set) => ({
  instrumentByTicketId: {},
  bondTicketsByTicketId: {},
  futureTicketsByTicketId: {},
  bondOptionTicketsByTicketId: {},
  swapTicketsByTicketId: {},
  swaptionTicketsByTicketId: {},
  repoTicketsByTicketId: {},
  isSendingByTicketId: {},
  lastSentByTicketId: {},

  // Switching instrument leaves both tickets alone, so flicking to Future and back
  // finds the half-written bond still there.
  setTicketInstrument: (ticketId, instrument) =>
    set((state) => ({
      instrumentByTicketId: {
        ...state.instrumentByTicketId,
        [ticketId]: instrument,
      },
    })),

  // Seeds an empty ticket on first mount. Never overwrites an existing one, so it is
  // safe to call on every mount - a widget remounted by a tab switch keeps its ticket.
  ensureBondTicket: (ticketId) =>
    set((state) =>
      state.bondTicketsByTicketId[ticketId]
        ? state
        : {
            bondTicketsByTicketId: {
              ...state.bondTicketsByTicketId,
              [ticketId]: returnEmptyBondTicket(),
            },
          },
    ),

  updateBondTicket: (ticketId, changes) =>
    set((state) => ({
      bondTicketsByTicketId: {
        ...state.bondTicketsByTicketId,
        [ticketId]: {
          ...(state.bondTicketsByTicketId[ticketId] ?? returnEmptyBondTicket()),
          ...changes,
        },
      },
    })),

  // A sent ticket is cleared, which also re-dates it - the next one is a new trade.
  resetBondTicket: (ticketId) =>
    set((state) => ({
      bondTicketsByTicketId: {
        ...state.bondTicketsByTicketId,
        [ticketId]: returnEmptyBondTicket(),
      },
    })),

  ensureFutureTicket: (ticketId) =>
    set((state) =>
      state.futureTicketsByTicketId[ticketId]
        ? state
        : {
            futureTicketsByTicketId: {
              ...state.futureTicketsByTicketId,
              [ticketId]: returnEmptyFutureTicket(),
            },
          },
    ),

  updateFutureTicket: (ticketId, changes) =>
    set((state) => ({
      futureTicketsByTicketId: {
        ...state.futureTicketsByTicketId,
        [ticketId]: {
          ...(state.futureTicketsByTicketId[ticketId] ??
            returnEmptyFutureTicket()),
          ...changes,
        },
      },
    })),

  resetFutureTicket: (ticketId) =>
    set((state) => ({
      futureTicketsByTicketId: {
        ...state.futureTicketsByTicketId,
        [ticketId]: returnEmptyFutureTicket(),
      },
    })),

  ensureBondOptionTicket: (ticketId) =>
    set((state) =>
      state.bondOptionTicketsByTicketId[ticketId]
        ? state
        : {
            bondOptionTicketsByTicketId: {
              ...state.bondOptionTicketsByTicketId,
              [ticketId]: returnEmptyBondOptionTicket(),
            },
          },
    ),

  updateBondOptionTicket: (ticketId, changes) =>
    set((state) => ({
      bondOptionTicketsByTicketId: {
        ...state.bondOptionTicketsByTicketId,
        [ticketId]: {
          ...(state.bondOptionTicketsByTicketId[ticketId] ??
            returnEmptyBondOptionTicket()),
          ...changes,
        },
      },
    })),

  resetBondOptionTicket: (ticketId) =>
    set((state) => ({
      bondOptionTicketsByTicketId: {
        ...state.bondOptionTicketsByTicketId,
        [ticketId]: returnEmptyBondOptionTicket(),
      },
    })),

  ensureSwapTicket: (ticketId) =>
    set((state) =>
      state.swapTicketsByTicketId[ticketId]
        ? state
        : {
            swapTicketsByTicketId: {
              ...state.swapTicketsByTicketId,
              [ticketId]: returnEmptySwapTicket(),
            },
          },
    ),

  updateSwapTicket: (ticketId, changes) =>
    set((state) => ({
      swapTicketsByTicketId: {
        ...state.swapTicketsByTicketId,
        [ticketId]: {
          ...(state.swapTicketsByTicketId[ticketId] ?? returnEmptySwapTicket()),
          ...changes,
        },
      },
    })),

  updateSwapLeg: (ticketId, leg, changes) =>
    set((state) => {
      const ticket =
        state.swapTicketsByTicketId[ticketId] ?? returnEmptySwapTicket();
      return {
        swapTicketsByTicketId: {
          ...state.swapTicketsByTicketId,
          [ticketId]: { ...ticket, [leg]: { ...ticket[leg], ...changes } },
        },
      };
    }),

  resetSwapTicket: (ticketId) =>
    set((state) => ({
      swapTicketsByTicketId: {
        ...state.swapTicketsByTicketId,
        [ticketId]: returnEmptySwapTicket(),
      },
    })),

  ensureSwaptionTicket: (ticketId) =>
    set((state) =>
      state.swaptionTicketsByTicketId[ticketId]
        ? state
        : {
            swaptionTicketsByTicketId: {
              ...state.swaptionTicketsByTicketId,
              [ticketId]: returnEmptySwaptionTicket(),
            },
          },
    ),

  updateSwaptionTicket: (ticketId, changes) =>
    set((state) => ({
      swaptionTicketsByTicketId: {
        ...state.swaptionTicketsByTicketId,
        [ticketId]: {
          ...(state.swaptionTicketsByTicketId[ticketId] ??
            returnEmptySwaptionTicket()),
          ...changes,
        },
      },
    })),

  updateSwaptionLeg: (ticketId, leg, changes) =>
    set((state) => {
      const ticket =
        state.swaptionTicketsByTicketId[ticketId] ?? returnEmptySwaptionTicket();
      return {
        swaptionTicketsByTicketId: {
          ...state.swaptionTicketsByTicketId,
          [ticketId]: { ...ticket, [leg]: { ...ticket[leg], ...changes } },
        },
      };
    }),

  resetSwaptionTicket: (ticketId) =>
    set((state) => ({
      swaptionTicketsByTicketId: {
        ...state.swaptionTicketsByTicketId,
        [ticketId]: returnEmptySwaptionTicket(),
      },
    })),

  ensureRepoTicket: (ticketId) =>
    set((state) =>
      state.repoTicketsByTicketId[ticketId]
        ? state
        : {
            repoTicketsByTicketId: {
              ...state.repoTicketsByTicketId,
              [ticketId]: returnEmptyRepoTicket(),
            },
          },
    ),

  updateRepoTicket: (ticketId, changes) =>
    set((state) => ({
      repoTicketsByTicketId: {
        ...state.repoTicketsByTicketId,
        [ticketId]: {
          ...(state.repoTicketsByTicketId[ticketId] ?? returnEmptyRepoTicket()),
          ...changes,
        },
      },
    })),

  resetRepoTicket: (ticketId) =>
    set((state) => ({
      repoTicketsByTicketId: {
        ...state.repoTicketsByTicketId,
        [ticketId]: returnEmptyRepoTicket(),
      },
    })),

  setIsSendingTicket: (ticketId, isSending) =>
    set((state) => ({
      isSendingByTicketId: {
        ...state.isSendingByTicketId,
        [ticketId]: isSending,
      },
    })),

  // Replaces any confirmation still showing, so a second send never queues behind the
  // first. The widget owns the countdown that clears it.
  setLastSentTicket: (ticketId, lastSent) =>
    set((state) => ({
      lastSentByTicketId: { ...state.lastSentByTicketId, [ticketId]: lastSent },
    })),

  clearLastSentTicket: (ticketId) =>
    set((state) => {
      if (!state.lastSentByTicketId[ticketId]) return state;
      const lastSentByTicketId = { ...state.lastSentByTicketId };
      delete lastSentByTicketId[ticketId];
      return { lastSentByTicketId };
    }),

  // Called when the owner goes (widget deleted, draft leg saved or discarded), never on
  // unmount - unmount also fires on a tab switch mid-ticket.
  removeTickets: (ticketId) =>
    set((state) => {
      const instrumentByTicketId = { ...state.instrumentByTicketId };
      const bondTicketsByTicketId = { ...state.bondTicketsByTicketId };
      const futureTicketsByTicketId = { ...state.futureTicketsByTicketId };
      const bondOptionTicketsByTicketId = {
        ...state.bondOptionTicketsByTicketId,
      };
      const swapTicketsByTicketId = { ...state.swapTicketsByTicketId };
      const swaptionTicketsByTicketId = { ...state.swaptionTicketsByTicketId };
      const repoTicketsByTicketId = { ...state.repoTicketsByTicketId };
      const isSendingByTicketId = { ...state.isSendingByTicketId };
      const lastSentByTicketId = { ...state.lastSentByTicketId };
      delete instrumentByTicketId[ticketId];
      delete bondTicketsByTicketId[ticketId];
      delete futureTicketsByTicketId[ticketId];
      delete bondOptionTicketsByTicketId[ticketId];
      delete swapTicketsByTicketId[ticketId];
      delete swaptionTicketsByTicketId[ticketId];
      delete repoTicketsByTicketId[ticketId];
      delete isSendingByTicketId[ticketId];
      delete lastSentByTicketId[ticketId];
      return {
        instrumentByTicketId,
        bondTicketsByTicketId,
        futureTicketsByTicketId,
        bondOptionTicketsByTicketId,
        swapTicketsByTicketId,
        swaptionTicketsByTicketId,
        repoTicketsByTicketId,
        isSendingByTicketId,
        lastSentByTicketId,
      };
    }),
}));

// LQC-2941 - one ticket by id whatever its instrument, for callers (Trade Creation draft
// legs) that hold the instrument rather than a typed ticket.
export function returnTicketForInstrument(
  state: State,
  instrument: TicketInstrument,
  ticketId: string,
) {
  if (instrument === TicketInstrument.Future) return state.futureTicketsByTicketId[ticketId];
  if (instrument === TicketInstrument.BondOption) return state.bondOptionTicketsByTicketId[ticketId];
  if (instrument === TicketInstrument.Swap) return state.swapTicketsByTicketId[ticketId];
  if (instrument === TicketInstrument.Swaption) return state.swaptionTicketsByTicketId[ticketId];
  if (instrument === TicketInstrument.Repo) return state.repoTicketsByTicketId[ticketId];
  return state.bondTicketsByTicketId[ticketId];
}

export function ensureTicketForInstrument(
  ticketId: string,
  instrument: TicketInstrument,
) {
  const store = tradeFromScratchStore.getState();
  store.setTicketInstrument(ticketId, instrument);
  if (instrument === TicketInstrument.Future) return store.ensureFutureTicket(ticketId);
  if (instrument === TicketInstrument.BondOption) return store.ensureBondOptionTicket(ticketId);
  if (instrument === TicketInstrument.Swap) return store.ensureSwapTicket(ticketId);
  if (instrument === TicketInstrument.Swaption) return store.ensureSwaptionTicket(ticketId);
  if (instrument === TicketInstrument.Repo) return store.ensureRepoTicket(ticketId);
  return store.ensureBondTicket(ticketId);
}

// Changes are typed loosely on purpose: the caller built them for this instrument.
export function updateTicketForInstrument(
  ticketId: string,
  instrument: TicketInstrument,
  changes: object,
) {
  const store = tradeFromScratchStore.getState();
  if (instrument === TicketInstrument.Future) return store.updateFutureTicket(ticketId, changes);
  if (instrument === TicketInstrument.BondOption) return store.updateBondOptionTicket(ticketId, changes);
  if (instrument === TicketInstrument.Swap) return store.updateSwapTicket(ticketId, changes);
  if (instrument === TicketInstrument.Swaption) return store.updateSwaptionTicket(ticketId, changes);
  if (instrument === TicketInstrument.Repo) return store.updateRepoTicket(ticketId, changes);
  return store.updateBondTicket(ticketId, changes);
}

export default tradeFromScratchStore;
